import {
  CalculationPayload,
  CalculationResponse,
  ColumnFilter,
  DocumentQuery,
  FileUploadInfo,
  ICalculationService,
  IDocumentService,
  IExportService,
  IFileAccessService,
  ILookupService,
  LookupRecord,
  SortOrder,
} from '@price-for-profit/micro-services';
import { CLIENT_ID, QUOTES_KEY, productRecommendations } from 'shared/constants';
import {
  BrandContact,
  BrandContactLookup,
  Brands,
  Currencies,
  CurrencyConversionToLookup,
  CustomerShipTo,
  CustomerShipToLookup,
  ExpenseOptionsToLookup,
  Quote,
  QuoteWithoutId,
  SearchableFields,
  SortableFields,
  CustomerProductNumbersLookup,
  DownloadFileProps,
  ProductLookup,
  ProductRecommendationLookup,
} from 'shared/types';
import { isAxiosError } from 'shared/utility';

export interface IQuoteService {
  getQuotes(props: GetQuotesProps): Promise<GetQuotesResponse>;
  getReminderQuotes(props: GetReminderQuotesProps): Promise<GetQuotesResponse>;
  createQuote(quote: QuoteWithoutId): Promise<Quote>;
  getQuote(id: string): Promise<Quote>;
  deleteQuote(id: string): Promise<void>;
  deleteQuoteFile: (fileId: string) => Promise<boolean>;
  updateQuote(quote: Quote): Promise<{ operationId: string }>;
  downloadQuoteFile: (props: DownloadFileProps) => Promise<void>;
  uploadQuoteFile: (files: FormData) => Promise<FileUploadInfo[]>;
  calculate<I, O>(props: CalculateProps<I>): Promise<CalculationResponse<O>>;
  downloadPdf<T>(props: DownloadPdfProps<T>): Promise<void>;
  autoCompleteSearch<T extends { brand: Brands }>(
    props: AutoCompleteSearchProps<T>
  ): Promise<Record<keyof T, string>[]>;
  getDropdownOptions<T extends { brand: Brands }>(props: GetDropdownOptionsProps<T>): Promise<string[]>;

  getBrandContactInfo(brand: Brands): Promise<BrandContact[]>;
  getCustomerShipTo(filterBrand: Brands, filterCustomerId: string): Promise<CustomerShipTo[]>;
  getProduct(brand: string, vendorId: string, itemId: string): Promise<LookupRecord<ProductLookup> | undefined>;
  getProductRecommendations(customername: string): Promise<ProductRecommendationLookup[]>;
  getExchangeRate(currency: Currencies | string): Promise<string>;
  getExpenseOptions(): Promise<string[]>;
  getCustomerProductNumbers(customerId: string, productId: string): Promise<string[]>;
}

export interface GetQuotesProps {
  brand: Brands;
  page: number;
  pageSize: number;
  sortBy: SortableFields;
  sortOrder: SortOrder;
  search: string;
  searchColumn: SearchableFields;
  salesRepOnly: boolean;
  salesRep: string;
}

export interface GetReminderQuotesProps {
  salesRep: string | undefined;
  brand: string;
}

export interface GetQuotesResponse {
  data: Quote[];
  totalCount: number;
  totalPages: number;
}

export interface CalculateProps<T> {
  modelId: string;
  payload: CalculationPayload<T>;
}

export interface DownloadPdfProps<T> {
  exportId: string;
  fileName?: string;
  orientation?: 'landscape' | 'portrait';
  data: T;
}

interface AutoCompleteSearchProps<T> {
  query: string;
  brand: Brands;
  table: string;
  searchColumn: keyof T;
}

interface GetDropdownOptionsProps<T> {
  brand: Brands;
  table: string;
  optionColumn: keyof T;
}

export class QuoteService implements IQuoteService {
  constructor(
    private fileAccessService: IFileAccessService,
    private documentService: IDocumentService,
    private calculationService: ICalculationService,
    private exportService: IExportService,
    private lookupService: ILookupService
  ) {}

  async getQuote(id: string): Promise<Quote> {
    const { data } = await this.documentService.getById<Quote>({ clientId: CLIENT_ID, type: QUOTES_KEY, id });
    return data;
  }

  async uploadQuoteFile(files: FormData): Promise<FileUploadInfo[]> {
    return await this.fileAccessService.uploadFile({
      clientId: CLIENT_ID,
      files,
    });
  }
  async getQuotes({
    brand,
    page,
    pageSize,
    sortBy,
    sortOrder,
    search,
    searchColumn,
    salesRepOnly,
    salesRep,
  }: GetQuotesProps): Promise<GetQuotesResponse> {
    const query: DocumentQuery<Quote> = {
      document: {
        brand: [
          {
            operator: 'eq',
            value: brand,
          },
        ],
        [searchColumn]: [
          {
            operator: 'contains',
            value: search.toUpperCase(),
          },
        ],
      },
      sortBy,
      sortOrder,
      pageSize,
      page: page - 1, // page in the DocService is zero-based.
    };

    if (!search) {
      query.document[searchColumn] = undefined;
    }

    if (salesRepOnly) {
      query.document.salesRep = [
        {
          operator: 'eq',
          value: salesRep.toUpperCase(),
        },
      ];
    }
    const { data, metadata } = await this.documentService.query<Quote>({
      clientId: CLIENT_ID,
      type: QUOTES_KEY,
      payload: query,
    });
    const { totalCount, totalPages } = metadata;
    return { data, totalCount, totalPages };
  }

  async getReminderQuotes({ brand, salesRep }: GetReminderQuotesProps): Promise<GetQuotesResponse> {
    const query: DocumentQuery<Quote> = {
      document: {
        brand: [
          {
            operator: 'eq',
            value: brand,
          },
        ],
        reminderDate: [
          {
            operator: 'lte',
            value: new Date().toISOString(),
          },
        ],
        salesRep: [
          {
            operator: 'eq',
            value: salesRep?.toUpperCase() || '',
          },
        ],
      },
      sortBy: 'reminderDate',
      sortOrder: 'desc',
      page: 0,
    };
    const { data, metadata } = await this.documentService.query<Quote>({
      clientId: CLIENT_ID,
      type: QUOTES_KEY,
      payload: query,
    });
    const { totalCount, totalPages } = metadata;
    return { data, totalCount, totalPages };
  }

  async createQuote(quote: QuoteWithoutId): Promise<Quote> {
    const { data } = await this.documentService.create<QuoteWithoutId, Quote>({
      clientId: CLIENT_ID,
      type: QUOTES_KEY,
      payload: quote,
    });
    return data;
  }
  async updateQuote(quote: Quote) {
    const { metadata } = await this.documentService.update<Quote>({
      clientId: CLIENT_ID,
      type: QUOTES_KEY,
      id: quote.id,
      payload: quote,
    });

    return metadata;
  }

  async getProduct(brand: string, vendorId: string, itemId: string): Promise<LookupRecord<ProductLookup> | undefined> {
    try {
      const columnFilters: ColumnFilter<ProductLookup>[] = [
        {
          columnName: 'brand',
          dataType: 'string',
          queryOperator: 'eq',
          value: brand.toUpperCase(),
        },
        {
          columnName: 'vendorid',
          dataType: 'string',
          queryOperator: 'eq',
          value: vendorId,
        },
        {
          columnName: 'itemid',
          dataType: 'string',
          queryOperator: 'eq',
          value: itemId,
        },
      ];

      const data = await this.lookupService.filteredAndSearch({
        clientId: CLIENT_ID,
        table: 'products',
        columnFilters,
      });

      return data[0];
    } catch (error) {
      if (isAxiosError(error)) {
        if (error?.response?.status === 404) {
          console.log('Product could not be found');
        }
        return undefined;
      }
      return undefined;
    }
  }

  async deleteQuote(id: string) {
    await this.documentService.delete({ clientId: CLIENT_ID, type: QUOTES_KEY, id });
  }

  async deleteQuoteFile(fileId: string): Promise<boolean> {
    await this.fileAccessService.deleteFile({
      clientId: CLIENT_ID,
      fileName: fileId,
    });

    return true;
  }

  async calculate<I, O>({ modelId, payload }: CalculateProps<I>) {
    return await this.calculationService.calculate<I, O>({ clientId: CLIENT_ID, modelId, payload });
  }

  async downloadPdf<T>({ exportId, data, fileName = 'export', orientation = 'portrait' }: DownloadPdfProps<T>) {
    const blob = await this.exportService.downloadPdf<T>({
      clientId: CLIENT_ID,
      templateId: exportId,
      payload: { orientation, data },
    });

    const blobUrl = URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', `${fileName}.pdf`);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(link.href);
    link.parentNode?.removeChild(link);
  }

  async getBrandContactInfo(brand: Brands): Promise<BrandContact[]> {
    try {
      const columnFilters: ColumnFilter<BrandContactLookup>[] = [
        {
          columnName: 'brand',
          dataType: 'string',
          queryOperator: 'eq',
          value: brand.toUpperCase(),
        },
      ];
      const data = await this.lookupService.filteredAndSearch({
        clientId: CLIENT_ID,
        table: 'brandcontactinfo',
        columnFilters,
      });
      // const { uniqueid, brand: _, ...brandContact } = data[0];
      // return brandContact;

      const contactInfo = data.map<BrandContact>(({ name, address1, address2, phone, fax, contact, uniqueid }) => ({
        name: name,
        address1: address1,
        address2: address2,
        phone: phone,
        fax: fax,
        contact: contact,
        uniqueId: uniqueid,
      }));

      return contactInfo;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error?.response?.status === 404) {
          throw new Error('Brand Contact info could not be found');
        }
        throw error;
      }
      throw error;
    }
  }

  async downloadQuoteFile(props: DownloadFileProps): Promise<void> {
    const file = await this.fileAccessService.getFile({
      clientId: CLIENT_ID,
      fileName: props.fileId,
    });
    this.handleBlob(file, props.originalFileName);
  }

  private handleBlob(blob: Blob, fileName: string) {
    const blobUrl = URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(link.href);
    link.parentNode?.removeChild(link);
  }

  async getCustomerShipTo(filterBrand: Brands, filterCustomerId: string): Promise<CustomerShipTo[]> {
    try {
      const columnFilters: ColumnFilter<CustomerShipToLookup>[] = [
        {
          columnName: 'brand',
          dataType: 'string',
          queryOperator: 'eq',
          value: filterBrand.toUpperCase(),
        },
        {
          columnName: 'customerid',
          dataType: 'string',
          queryOperator: 'eq',
          value: filterCustomerId.toUpperCase(),
        },
      ];
      const data = await this.lookupService.filteredAndSearch({
        clientId: CLIENT_ID,
        table: 'customershipto',
        columnFilters,
      });

      const addresses = data
        .map<CustomerShipTo>(
          ({
            brand,
            trxcnt,
            customerid,
            shiptoaddressline1,
            shiptoaddressline2,
            shiptocity,
            shiptostate,
            shiptocountry,
            shiptozipcode,
            shiptoid,
            uniqueid,
          }) => ({
            brand,
            transactionCount: Number(trxcnt),
            customerId: customerid,
            shipToAddressLine1: shiptoaddressline1,
            shipToAddressLine2: shiptoaddressline2,
            shipToCity: shiptocity,
            shipToState: shiptostate,
            shipToCountry: shiptocountry,
            shipToZipCode: shiptozipcode,
            uniqueId: uniqueid,
            shipToId: shiptoid,
          })
        )
        .sort((one, two) => two.transactionCount - one.transactionCount);

      return addresses;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error?.response?.status === 404) {
          throw new Error('Customer Ship To info could not be found');
        }
        throw error;
      }
      throw error;
    }
  }

  async getExchangeRate(currency: Currencies | string): Promise<string> {
    try {
      const data = await this.lookupService.filteredAndSearch<CurrencyConversionToLookup>({
        clientId: CLIENT_ID,
        table: 'currencyconversion',
        columnFilters: [
          {
            columnName: 'currency',
            dataType: 'string',
            queryOperator: 'eq',
            value: currency,
          },
        ],
      });
      return data[0].exchangerate;
    } catch (error) {
      throw error;
    }
  }

  async getExpenseOptions(): Promise<string[]> {
    try {
      const data = await this.lookupService.filteredAndSearch<ExpenseOptionsToLookup>({
        clientId: CLIENT_ID,
        table: 'expensedropdown',
        columnFilters: [],
      });
      return data.map<string>(lookup => lookup['expense']).sort();
    } catch (error) {
      throw error;
    }
  }

  async getDropdownOptions<T extends { brand: Brands }>({ brand, table, optionColumn }: GetDropdownOptionsProps<T>) {
    try {
      const columnFilters: ColumnFilter<T>[] = [
        {
          columnName: 'brand',
          dataType: 'string',
          queryOperator: 'eq',
          value: brand.toUpperCase(),
        },
      ];
      const data = await this.lookupService.filteredAndSearch({ clientId: CLIENT_ID, table, columnFilters });
      return data.map<string>(lookup => lookup[optionColumn]).sort();
    } catch (error) {
      if (isAxiosError(error)) {
        if (error?.response?.status === 404) {
          throw new Error(`'${table}' options could not be found.`);
        }
        throw error;
      }
      throw error;
    }
  }

  async getCustomerProductNumbers(customerId: string, productId: string): Promise<string[]> {
    try {
      const data = await this.lookupService.filteredAndSearch<CustomerProductNumbersLookup>({
        clientId: CLIENT_ID,
        table: 'customerproducts',
        columnFilters: [
          {
            columnName: 'customerid',
            dataType: 'string',
            queryOperator: 'eq',
            value: customerId,
          },
          {
            columnName: 'productid',
            dataType: 'string',
            queryOperator: 'eq',
            value: productId,
          },
        ],
      });
      return data.map<string>(lookup => lookup['customerproductid']);
    } catch (error) {
      throw error;
    }
  }

  async autoCompleteSearch<T extends { brand: Brands }>({
    query,
    brand,
    searchColumn,
    table,
  }: AutoCompleteSearchProps<T>) {
    try {
      const columnFilters: ColumnFilter<T>[] = [
        {
          columnName: searchColumn,
          dataType: 'string',
          queryOperator: 'contains',
          value: query.toUpperCase(),
        },
        {
          columnName: 'brand',
          dataType: 'string',
          queryOperator: 'eq',
          value: brand.toUpperCase(),
        },
      ];
      const data = await this.lookupService.filteredAndSearch({ clientId: CLIENT_ID, table, columnFilters });

      return data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error?.response?.status === 404) {
          return [];
        }
        throw error;
      }
      throw error;
    }
  }

  getProductRecommendations(customername: string): Promise<ProductRecommendationLookup[]> {
    return Promise.all(productRecommendations.filter(p => p.customername === customername.toUpperCase()));
  }
}
